import React, { useEffect, useState } from "react";
import { Card } from "reactstrap";
import Modal from "react-bootstrap/Modal";
import { Button } from "react-bootstrap";
import { SearchIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import CommonTable from "../CommonTable";
import AddEquipment from "./AddEquipment";
import { useLoader } from "../../_helper/LoaderContext";
import {
    deleteEquipment,
    EQUIPMENT_CATEGORIES,
    getEquipmentList,
    toggleEquipmentStatus,
    viewEquipment,
} from "./service/equipment-service";

export default function EquipmentHome() {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const [show, setShow] = useState(false);
    const [edit, setEdit] = useState(false);
    const [view, setView] = useState(false);
    const [data, setData] = useState({});
    const [viewData, setViewData] = useState(null);
    const [equipment, setEquipment] = useState([]);
    const [search, setSearch] = useState("");
    const [category, setCategory] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(10);
    const [totalEntries, setTotalEntries] = useState(0);

    const columns = [
        { header: "Name", accessor: "name", width: "30%" },
        { header: "Code", accessor: "code", width: "20%" },
        {
            header: "Category",
            accessor: "category",
            width: "25%",
            render: (value) =>
                Array.isArray(value) ? value.join(", ") : value || "-",
        },
    ];

    const getList = async () => {
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("search", search);
            formData.append("page", currentPage);
            formData.append("limit", entriesPerPage);
            if (category) {
                formData.append("category", category);
            }
            const res = await getEquipmentList(formData);
            if (res.data.success === true) {
                const payload = res.data.data;
                setEquipment(payload?.equipments || []);
                setTotalEntries(payload?.totalCount || 0);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            getList();
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search, currentPage, entriesPerPage, category]);

    const totalPages = Math.ceil(totalEntries / entriesPerPage) || 1;

    const handleToggleStatus = (row) => {
        Swal.fire({
            title: "Are you sure?",
            text: row.isActive
                ? "You want to inactivate this equipment?"
                : "You want to activate this equipment?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: row.isActive === true ? "Inactive" : "Active",
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await toggleEquipmentStatus(row._id);
                    if (res.data.success === true) {
                        toast.success(res.data.message);
                        getList();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleView = async (row) => {
        setLoading(true);
        try {
            const res = await viewEquipment(row._id);
            if (res.data.success === true) {
                setViewData(res.data.data || row);
                setView(true);
            } else {
                setViewData(row);
                setView(true);
            }
        } catch (error) {
            console.log(error);
            setViewData(row);
            setView(true);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (row) => {
        setData(row);
        setEdit(true);
    };

    const handleDelete = (row) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You want to delete this equipment?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#3085d6",
            cancelButtonColor: "#d33",
            confirmButtonText: "Yes, delete it!",
        }).then(async (result) => {
            if (result.isConfirmed) {
                setLoading(true);
                try {
                    const res = await deleteEquipment(row._id);
                    if (res.data.success === true) {
                        toast.success(res.data.message);
                        getList();
                    }
                } catch (error) {
                    console.log(error);
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const actions = [
        { label: "View", onClick: (row) => handleView(row) },
        { label: "Edit", onClick: (row) => handleEdit(row) },
        { label: "Delete", onClick: (row) => handleDelete(row) },
    ];

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);
    const editClose = () => setEdit(false);
    const viewClose = () => {
        setView(false);
        setViewData(null);
    };

    return (
        <>
            <Card>
                <div className="px-3 mt-4">
                    <div className="row">
                        <div className="col-md-4">
                            <div className="input-group mb-3">
                                <span className="input-group-text tw-bg-white" id="equipment-search">
                                    <SearchIcon />
                                </span>
                                <input
                                    type="text"
                                    className="form-control icon_form"
                                    placeholder="Search by name or code..."
                                    aria-describedby="equipment-search"
                                    style={{ borderLeft: "none" }}
                                    value={search}
                                    onChange={(e) => {
                                        setSearch(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                />
                            </div>
                        </div>
                        <div className="col-md-4">
                            <select
                                className="form-control mb-3"
                                value={category}
                                onChange={(e) => {
                                    setCategory(e.target.value);
                                    setCurrentPage(1);
                                }}
                            >
                                <option value="">All Categories</option>
                                {EQUIPMENT_CATEGORIES.map((cat) => (
                                    <option key={cat} value={cat}>
                                        {cat}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4 text-end">
                            <button className="btn btn-primary" onClick={handleShow}>
                                {t("Add")}
                            </button>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-12">
                            <CommonTable
                                columns={columns}
                                data={equipment}
                                onToggleStatus={handleToggleStatus}
                                onAction={actions}
                                totalPages={totalPages}
                                currentPage={currentPage}
                                setCurrentPage={setCurrentPage}
                                entriesPerPage={entriesPerPage}
                                setEntriesPerPage={setEntriesPerPage}
                            />
                        </div>
                    </div>
                </div>
            </Card>

            <Modal show={show} onHide={handleClose} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Add Equipment</Modal.Title>
                </Modal.Header>
                <AddEquipment key={show ? "add-open" : "add-closed"} handleClose={handleClose} getList={getList} />
            </Modal>

            <Modal show={edit} onHide={editClose} centered size="md">
                <Modal.Header closeButton>
                    <Modal.Title>Edit Equipment</Modal.Title>
                </Modal.Header>
                <AddEquipment handleClose={editClose} data={data} getList={getList} />
            </Modal>

            <Modal show={view} onHide={viewClose} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Equipment Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {viewData && (
                        <div className="row g-3">
                            <div className="col-md-6">
                                <small className="text-muted d-block">Name</small>
                                <strong>{viewData.name || "-"}</strong>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted d-block">Code</small>
                                <strong>{viewData.code || "-"}</strong>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted d-block">Category</small>
                                <strong>
                                    {Array.isArray(viewData.category)
                                        ? viewData.category.join(", ")
                                        : viewData.category || "-"}
                                </strong>
                            </div>
                            <div className="col-md-6">
                                <small className="text-muted d-block">Status</small>
                                <strong>
                                    {viewData.isActive === true ? "Active" : "Inactive"}
                                </strong>
                            </div>
                            <div className="col-12">
                                <small className="text-muted d-block">Description</small>
                                <p className="mb-0">{viewData.description || "-"}</p>
                            </div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={viewClose}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
